import { KeyedObject } from 'bernie-core/dist/source';
import {
  AnalyticsStore,
  codeSplit,
  ContextStore,
  Controller,
  ExperimentStore,
  FetchOptions,
  PageStore,
  timeoutPromise
} from 'bernie-core';
import { getTrackingInfoForLogging } from '../common/logging-utils';
import { isExperimentEnabled } from '../helpers/experiment-helper';
import { ActivitySort } from '../common/__generated__/api';
import { handleInvalidDates } from '../helpers/controller-date-helper';
import { ActivitySearchStore, SearchCriteriaStore, ShortlistStore, SearchQueryObject } from '../stores';

export class ActivitySearchController implements Controller {
  public pageId = 'page.LX-Search,A,20';
  public path = '/things-to-do/search';
  public routeName = 'activity-search';
  public bundles: string[] = [];

  /**
   * Components may be referred to directly
   *   `public component = ExamplePage;`
   *
   * Or, the preferred option is to create a code split point that will generate a new bundle.
   *   Use webpacks magic comments `webpackChunkName: "INSERT_FRIENDLY_NAME_HERE"`
   *   This will give it a friendly name for the name of the file.
   */
  // eslint-disable-next-line import/no-unresolved
  public component = codeSplit(() => import(/* webpackChunkName: "search-page" */ 'views/activity-search-page'));
  public exact = true;

  public async fetch(options: FetchOptions): Promise<any> {
    if (!options || !options.stores) {
      return Promise.reject('ActivitiesSearch.fetch received invalid FetchOptions');
    }
    const { stores, query } = options;
    const activitySearchStore: ActivitySearchStore = stores.get<ActivitySearchStore>('activitySearchStore');
    const searchCriteriaStore: SearchCriteriaStore = stores.get<SearchCriteriaStore>('searchCriteriaStore');
    const shortlistStore: ShortlistStore = stores.get<ShortlistStore>('shortlistStore');
    const context: ContextStore = stores.get<ContextStore>('context');
    const analyticsStore: AnalyticsStore = stores.get<AnalyticsStore>('analytics');
    const experimentStore: ExperimentStore = stores.get<ExperimentStore>('experiment');
    const page: PageStore = stores.get<PageStore>('page');
    const config: KeyedObject = (context as any).config;
    activitySearchStore.query = query;
    activitySearchStore.serverSide = options.isServer;

    searchCriteriaStore.localDateFormat = context.localDateFormat;
    searchCriteriaStore.setExperimentStore(experimentStore);
    this.populateDefaultSortIfApplicable(searchCriteriaStore, query);
    const isQueryKeysDifferent = query && searchCriteriaStore.isQueryKeysDifferent(query);

    if (experimentStore && experimentStore.exposures && experimentStore.exposures.LX_PWA_SRP_M1) {
      experimentStore.logExperiment(context, page, experimentStore.exposures.LX_PWA_SRP_M1);
    }

    activitySearchStore.isGridLayoutListViewEnabled = isExperimentEnabled(experimentStore, 'LX_PWA_SRP_GRID_VIEW');

    const isNewFetchRequired = isQueryKeysDifferent || activitySearchStore.searchError;
    if (isNewFetchRequired) {
      activitySearchStore.retainBrowserFocus = searchCriteriaStore.isSortOrFilterChanged(query);
      // We should reset analytics only for a "new" content refresh being done on client side
      const isDateOrLocationChanged = searchCriteriaStore.isDateOrLocationChanged(query);
      if (isDateOrLocationChanged) {
        searchCriteriaStore.resetQueryObject(query);
        this.populateDefaultSortIfApplicable(searchCriteriaStore, query);
      }
      if (!options.isServer && analyticsStore) {
        analyticsStore.resetAnalyticsForNewPage();
      }
      searchCriteriaStore.resetSearchCriteriaStore();
      searchCriteriaStore.setContext(context);
      searchCriteriaStore.searchQuery = query as SearchQueryObject;

      searchCriteriaStore.searchQuery = handleInvalidDates(
        query,
        searchCriteriaStore.searchQuery,
        searchCriteriaStore.localDateFormat
      ) as SearchQueryObject;

      shortlistStore.setContext(context);

      activitySearchStore.resetActivitySearchStore();

      if (
        searchCriteriaStore.searchQuery &&
        (searchCriteriaStore.searchQuery.location ||
          searchCriteriaStore.searchQuery.rid ||
          searchCriteriaStore.searchQuery.landingPage)
      ) {
        searchCriteriaStore.populateRegionTypeForLandingPage(query);
        const searchVariables = searchCriteriaStore.searchVariables;

        return timeoutPromise(
          Promise.all([
            activitySearchStore
              .fetchActivitiesSearchDestinationView(
                searchCriteriaStore,
                analyticsStore,
                query,
                searchVariables,
                this.pageId,
                null,
                true
              )
              .then(() => {
                if (activitySearchStore.searchSummary && activitySearchStore.searchSummary.pageInfo.title) {
                  page.title = activitySearchStore.searchSummary.pageInfo.title;
                }
              }),
            shortlistStore.fetchShortlist(options, this.pageId)
          ]),
          options.isServer ? config.timeout.serverControllerFetch : config.timeout.clientControllerFetch,
          `controller=search fetch=page timeout ${getTrackingInfoForLogging(context)}`
        );
      }
    }

    return Promise.resolve();
  }

  private populateDefaultSortIfApplicable(searchCriteriaStore: SearchCriteriaStore, query: KeyedObject): void {
    if (query && !query.sort) {
      query.sort = ActivitySort.RECOMMENDED;
    }
    searchCriteriaStore.populateDefaultSortIfApplicable();
  }

  public fetchPageData(): Promise<{ title: string; pageId: string }> {
    return Promise.resolve({ title: '', pageId: this.pageId });
  }
}
